import { Box, Stack, Typography } from '@mui/material'

import { Alert, Button } from '@repo/ui'
import { useCanAccess } from 'hooks'
import { useResolveSeoAlert, useSeoAlerts } from 'hooks/api'
import type { SeoAlert } from 'services'

import { dateTime } from '../shared'

const Facts = ({ alert }: { alert: SeoAlert }) => (
  <Stack spacing={1} sx={{ mt: 1 }}>
    <Typography variant='body3'>{alert.summary}</Typography>
    <Box
      component='dl'
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
        columnGap: 3,
        rowGap: 0.5,
      }}
    >
      {alert.facts.map((f) => (
        <Box key={f.label} sx={{ display: 'contents' }}>
          <Typography component='dt' variant='body3' color='text.secondary'>
            {f.label}
          </Typography>
          <Typography
            component='dd'
            variant='body3'
            sx={{ m: 0, wordBreak: 'break-word' }}
          >
            {f.value}
          </Typography>
        </Box>
      ))}
    </Box>
    <Typography variant='body3' color='text.secondary'>
      Open since {dateTime(alert.openedAt)}
      {alert.notifiedAt
        ? `. Emailed ${alert.notifyCount} time${
            alert.notifyCount === 1 ? '' : 's'
          }, last ${dateTime(alert.notifiedAt)}.`
        : '. Not emailed yet.'}
    </Typography>
  </Stack>
)

/**
 * VRG-729 — the SEO pull alerts, above every section of `/internal/gtm/seo`, so an open
 * incident is visible whichever tab the review email or a bookmark lands on.
 *
 * One alert per OPEN incident (`seo_alerts`): a scheduled pull that failed after its retries
 * or missed its slot, SE Ranking rejecting the account, a balance that will not cover the
 * next slot. Each one closes on its own when the next check succeeds; a platform admin can
 * also mark it resolved (no email, and it does not reopen for the same failure). Incidents
 * resolved in the last three days show as one quiet line, so 'it went away' is visible too.
 *
 * Renders nothing when there is nothing to say, including on an API whose scheduled jobs
 * are off (where no alert is ever evaluated).
 */
export const SeoAlertsBanner = () => {
  const { data } = useSeoAlerts()
  const resolve = useResolveSeoAlert()
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  if (!data) return null
  const resolved = data.recentlyResolved
  if (!data.open.length && !resolved.length) return null

  return (
    <Stack spacing={3}>
      {data.open.map((a) => (
        <Alert
          key={a.id}
          severity={a.severity}
          title={a.title}
          description={<Facts alert={a} />}
          button={
            <Button
              variant='secondary'
              size='small'
              disabled={!isAdmin || resolve.isPending}
              disabledReason={
                !isAdmin
                  ? 'Only platform admins can resolve SEO alerts.'
                  : undefined
              }
              loading={resolve.isPending && resolve.variables?.id === a.id}
              onClick={() => resolve.mutate({ id: a.id })}
            >
              Mark resolved
            </Button>
          }
        />
      ))}
      {resolved.length ? (
        <Alert
          severity='success'
          title={`Resolved: ${resolved.map((a) => a.title).join('; ')}`}
          description={`Closed ${resolved
            .map(
              (a) =>
                `${dateTime(a.resolvedAt)}${
                  a.resolvedBy && a.resolvedBy !== 'auto'
                    ? ` by ${a.resolvedBy}`
                    : ' (the next check succeeded)'
                }`
            )
            .join('; ')}.`}
        />
      ) : null}
    </Stack>
  )
}
